import { useState } from 'react'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'

interface EventCreateModalProps {
  onClose: () => void
  onCreate: (data: {
    name: string
    description?: string
    starts_at: string
    city?: string
    state?: string
    categories?: string[]
    url?: string
    image_url?: string
  }) => Promise<void>
}

export function EventCreateModal({ onClose, onCreate }: EventCreateModalProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [startsAt, setStartsAt] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [categories, setCategories] = useState('')
  const [url, setUrl] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!name.trim() || !startsAt) {
      setError('Name and start date are required')
      return
    }
    setLoading(true)
    setError(null)
    try {
      await onCreate({
        name: name.trim(),
        description: description.trim() || undefined,
        starts_at: new Date(startsAt).toISOString(),
        city: city.trim() || undefined,
        state: state.trim() || undefined,
        categories: categories.trim()
          ? categories.split(',').map((c) => c.trim()).filter(Boolean)
          : undefined,
        url: url.trim() || undefined,
        image_url: imageUrl.trim() || undefined,
      })
      onClose()
    } catch {
      setError('Failed to create event')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white dark:bg-[#1a1520] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-gray-900 dark:text-white">Add Event</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}

        <div className="space-y-4">
          <CustomTextInput
            label="Name"
            placeholder="Event name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Event description"
              rows={3}
              className="w-full rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] px-4 py-2 text-sm text-gray-900 dark:text-white focus:border-[#503864] focus:outline-none focus:ring-2 focus:ring-[#503864]/20"
            />
          </div>
          <CustomTextInput
            label="Start Date"
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            required
          />
          <div className="grid grid-cols-2 gap-4">
            <CustomTextInput
              label="City"
              placeholder="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
            <CustomTextInput
              label="State"
              placeholder="State"
              value={state}
              onChange={(e) => setState(e.target.value)}
            />
          </div>
          <CustomTextInput
            label="Categories"
            placeholder="music, food, art (comma-separated)"
            value={categories}
            onChange={(e) => setCategories(e.target.value)}
          />
          <CustomTextInput
            label="URL"
            placeholder="https://..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <CustomTextInput
            label="Image URL"
            placeholder="https://..."
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
          />
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <CustomButton variant="secondary" onClick={onClose}>Cancel</CustomButton>
          <CustomButton variant="primary" onClick={handleSubmit} loading={loading}>Create Event</CustomButton>
        </div>
      </div>
    </div>
  )
}
